//频道
import React, { useCallback, useState, useEffect, useMemo, useRef } from "react"
import { useFocusEffect } from "~/ui/react-navigation-stub"
import { ActivityIndicator, SectionList, StyleSheet, TouchableOpacity, DeviceEventEmitter } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { useSafeAreaInsets } from "~/ui/safe-area"
import {
  StartFlex,
  ColumnFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  Button
} from "~components/El"
import { NoData } from "~components/Layout"
import { SearchBar } from "~components/SearchBar"
import { ChatTypes, Event, EventNames, FLAG_REPLY } from "~proto"
import { useStores } from "~models"
import { Avatar } from "~widgets/avatar"
import { useModal } from "~hooks/useModal"
import useGroupActions from "~hooks/useGroupActions"
import useNavigation from "~hooks/useNavigation"
import { globalSpinner, globalToast, formatMessageTime } from "~utils"
import {
  CHANNEL_LIST_API_TYPE,
  REFRESH_CHANNEL_LIST_EVENT,
  extractChannelListItems,
} from "./constants"

type SectionKey = "subscribed"

const channelIdentityKeys = (item: any): string[] => {
  const keys: string[] = []
  if (item?.id != null) keys.push(`id:${item.id}`)
  const cid = item?.chat_id ?? item?.name
  if (cid != null && String(cid).trim() !== "") {
    keys.push(`cid:${String(cid).trim()}`)
  }
  return keys
}

const matchChannel = (channel: any, ref: any) => {
  const refKeys = channelIdentityKeys(ref)
  const channelKeys = new Set(channelIdentityKeys(channel))
  return refKeys.some((key) => channelKeys.has(key))
}

const syncSectionsJoinedState = (sections: ReturnType<typeof createInitialSections>) => {
  const subscribedData = sections.find((section) => section.key === "subscribed")?.data ?? []
  const joinedKeys = new Set<string>()

  for (const item of subscribedData) {
    channelIdentityKeys(item).forEach((key) => joinedKeys.add(key))
  }

  return sections.map((section) => ({
    ...section,
    data: section.data.map((item) => {
      if (section.key === "subscribed") {
        return { ...item, is_joined: true }
      }
      const isJoined = channelIdentityKeys(item).some((key) => joinedKeys.has(key))
      return { ...item, is_joined: isJoined }
    }),
  }))
}

const hasIdentityOverlap = (item: any, keySet: Set<string>) =>
  channelIdentityKeys(item).some((key) => keySet.has(key))

type OptimisticExclusions = { excluded: Map<string, number> }

const pruneExpiredExclusions = (store: OptimisticExclusions) => {
  const now = Date.now()
  for (const [key, exp] of store.excluded) {
    if (exp <= now) store.excluded.delete(key)
  }
}

const isOptimisticallyExcluded = (item: any, store: OptimisticExclusions) => {
  pruneExpiredExclusions(store)
  return channelIdentityKeys(item).some((key) => store.excluded.has(key))
}

const markOptimisticallyUnsubscribed = (item: any, store: OptimisticExclusions) => {
  const expires = Date.now() + 60_000
  channelIdentityKeys(item).forEach((key) => store.excluded.set(key, expires))
}

const clearOptimisticExclusion = (item: any, store: OptimisticExclusions) => {
  channelIdentityKeys(item).forEach((key) => store.excluded.delete(key))
}

const filterExcludedApiItems = (items: any[], store: OptimisticExclusions) =>
  items.filter((item) => !isOptimisticallyExcluded(item, store))

/** 刷新已订阅列表时，保留接口尚未返回的本地新订阅项 */
const mergeSubscribedRefresh = (prevData: any[], apiData: any[], store: OptimisticExclusions) => {
  const filteredApi = filterExcludedApiItems(apiData, store)
  const apiKeySet = new Set<string>()
  for (const item of filteredApi) {
    channelIdentityKeys(item).forEach((key) => apiKeySet.add(key))
  }
  const pendingLocal = prevData.filter(
    (item) => item.is_joined && !hasIdentityOverlap(item, apiKeySet),
  )
  const normalizedApi = filteredApi.map((item) => ({ ...item, is_joined: true }))
  return [...pendingLocal, ...normalizedApi]
}

const SECTION_META: Record<
  SectionKey,
  { title: string; type: number; index: number }
> = {
  subscribed: {
    title: "已订阅频道",
    type: CHANNEL_LIST_API_TYPE.SUBSCRIBED,
    index: 0,
  },
}

const createInitialSections = () => [
  { key: "subscribed" as const, title: SECTION_META.subscribed.title, data: [] as any[] },
]

const createInitialPagination = () => ({
  subscribed: { page: 1, hasMore: true, loadingMore: false },
})

const Container = styled(ColumnFlex)`
  flex: 1;
  /* padding-bottom: 30px; */
`

const isChannelCreator = (item: any, userId?: number | null) => {
  if (!userId) return false
  if (item?.creator_id != null && Number(item.creator_id) === userId) return true
  if (item?.creator?.id != null && Number(item.creator.id) === userId) return true
  return false
}

const ChannelMembersItem = React.memo(
  ({
    item,
    onClick,
    onPressChat,
    isCreator,
  }: {
    item: any
    onClick: (v: any) => void
    onPressChat: (item: any) => void
    isCreator: (item: any) => boolean
  }) => {
  const theme = useTheme()
  const isCreatorJoined = item.is_joined && isCreator(item)
  return (
    <BetweenFlex height="auto" width="100%" padding="0 12px">
      <TouchableOpacity
        activeOpacity={item.is_joined ? 0.85 : 1}
        onClick={() => item.is_joined && onPressChat(item)}
        style={{ flex: 1 }}
      >
        <StartFlex height="auto" width="100%">
          <Avatar avatar={item.image} size={40} name={item.title} />
          <ColumnFlex flex={1} height="auto" ml="14px">
            <BetweenFlex width="100%" padding="15px 0">
              <ColumnFlex flex={3}>
                <Text color={theme.colors.text} fontSize="14px" fontWeight="600">
                  {item.title || ""}
                </Text>
                {item.desc && (
                  <Text numberOfLines={2} color={theme.colors.secondary} fontSize="12px" mt="2px">
                    {item.desc ||''}
                  </Text>
                )}
                <Text color={theme.colors.secondary} fontSize="12px" mt="4px">
                  {item.member_count} 订阅
                </Text>
              </ColumnFlex>
            </BetweenFlex>
            <CenterFlex height={StyleSheet.hairlineWidth} width="100%" bg={theme.colors.border} />
          </ColumnFlex>
        </StartFlex>
      </TouchableOpacity>
    <ColumnFlex width="auto" style={{ alignItems: "flex-end" }}>
        <Text color={theme.colors.secondaryText} fontSize="12px">
          {formatMessageTime(item.created)}
        </Text>
        <StartFlex width="auto" mt="10px">
        <IconFont name="xxtz" size={14} color={theme.colors.primary} />
        {isCreatorJoined ? (
          null
        ) : (
          <Button
            width="80px"
            height="auto"
            ml="12px"
            padding="5px 10px"
            borderRadius="6px"
            bg={item.is_joined ? theme.colors.grayBackground : theme.colors.primary}
            onClick={onClick}
          >
            <Text
              color={item.is_joined ? theme.colors.secondaryText : theme.colors.textLight}
              fontSize="14px"
            >
              {item.is_joined ? "已订阅" : "订阅"}
            </Text>
          </Button>
        )}
        </StartFlex>
    </ColumnFlex>
      
    </BetweenFlex>
  )
}, (prev, next) =>
  prev.item?.id === next.item?.id &&
  String(prev.item?.chat_id) === String(next.item?.chat_id) &&
  prev.item?.is_joined === next.item?.is_joined &&
  prev.item?.title === next.item?.title &&
  prev.item?.creator_id === next.item?.creator_id &&
  prev.isCreator === next.isCreator &&
  prev.onClick === next.onClick &&
  prev.onPressChat === next.onPressChat,
)

const ChannelList: React.FC<any> = ({route}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { apiStore, userStore, chatStore, eventStore } = useStores()
  const { Dialog } = useModal()
  const { handleMemberKickedOutGroup, handleAddGroupMember, ensureLocalChatWhenJoiningGroup } = useGroupActions()
  const { currentUser } = userStore
  const [searchText, setSearchText] = useState("")
  const [list, setList] = useState(createInitialSections)
  const [pagination, setPagination] = useState(createInitialPagination)
  const requestGuardRef = useRef<Record<SectionKey, number>>({
    subscribed: 0,
  })
  const refreshAllGuardRef = useRef(0)
  const canLoadMoreRef = useRef(false)
  const optimisticExclusionsRef = useRef<OptimisticExclusions>({ excluded: new Map() })
  const skipRemoteRefreshUntilRef = useRef(0)

  const LIMIT = 20

  const mergeUnique = useCallback((prev: any[], next: any[]) => {
    const map = new Map<string, any>()
    for (const item of prev || []) {
      const key = (item?.id ?? item?.chat_id ?? "")?.toString?.() || ""
      if (key) map.set(key, item)
    }
    for (const item of next || []) {
      const key = (item?.id ?? item?.chat_id ?? "")?.toString?.() || ""
      if (key) map.set(key, item)
    }
    return Array.from(map.values())
  }, [])

  const getSectionList = useCallback(async (
    sectionKey: SectionKey,
    options?: { silent?: boolean; page?: number; append?: boolean },
  ) => {
    const silent = options?.silent ?? false
    const nextPage = options?.page ?? 1
    const append = options?.append ?? false
    const { type, index } = SECTION_META[sectionKey]

    try {
      const reqId = Date.now()
      requestGuardRef.current[sectionKey] = reqId

      if (!silent && nextPage === 1) globalSpinner.show()
      if (nextPage > 1) {
        setPagination((prev) => ({
          ...prev,
          [sectionKey]: { ...prev[sectionKey], loadingMore: true },
        }))
      }

      const result = await apiStore.getJoinChatGroupAndChannelList({
        type,
        page: nextPage,
        limit: LIMIT,
        ...(silent ? { _t: Date.now() } : {}),
      })

      if (requestGuardRef.current[sectionKey] !== reqId) return

      const next = extractChannelListItems(result)
      setList((prev) => {
        const sections = [...prev]
        const current = sections[index]?.data || []
        const nextData = append
          ? mergeUnique(current, filterExcludedApiItems(next, optimisticExclusionsRef.current))
          : sectionKey === "subscribed"
            ? mergeSubscribedRefresh(current, next, optimisticExclusionsRef.current)
            : filterExcludedApiItems(next, optimisticExclusionsRef.current)
        sections[index] = {
          ...sections[index],
          data: nextData,
        }
        return syncSectionsJoinedState(sections)
      })
      setPagination((prev) => ({
        ...prev,
        [sectionKey]: {
          page: nextPage,
          hasMore: Array.isArray(next) ? next.length >= LIMIT : false,
          loadingMore: false,
        },
      }))
    } catch (error) {
      setPagination((prev) => ({
        ...prev,
        [sectionKey]: { ...prev[sectionKey], loadingMore: false },
      }))
    } finally {
      if (!silent && nextPage === 1) globalSpinner.close()
    }
  }, [LIMIT, apiStore, mergeUnique])

  const refreshSubscribedList = useCallback((silent = true) => {
    return getSectionList("subscribed", { silent, page: 1, append: false })
  }, [getSectionList])

  const refreshAllSections = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    const silent = options?.silent ?? false
    if (!options?.force && Date.now() < skipRemoteRefreshUntilRef.current) return
    const reqId = Date.now()
    refreshAllGuardRef.current = reqId

    try {
      if (!silent) globalSpinner.show()
      const subscribedRes = await apiStore.getJoinChatGroupAndChannelList({
        type: CHANNEL_LIST_API_TYPE.SUBSCRIBED,
        page: 1,
        limit: LIMIT,
      })

      if (refreshAllGuardRef.current !== reqId) return

      const subscribedItems = extractChannelListItems(subscribedRes)

      setList((prev) => {
        const sections = [...prev]
        const subscribedIndex = SECTION_META.subscribed.index
        sections[subscribedIndex] = {
          ...sections[subscribedIndex],
          data: mergeSubscribedRefresh(
            sections[subscribedIndex]?.data || [],
            subscribedItems,
            optimisticExclusionsRef.current,
          ),
        }
        return syncSectionsJoinedState(sections)
      })
      setPagination({
        subscribed: {
          page: 1,
          hasMore: subscribedItems.length >= LIMIT,
          loadingMore: false,
        },
      })
    } catch (error: any) {
      if (!silent) {
        globalToast.show(error?.message || "频道列表加载失败")
      }
    } finally {
      if (!silent) globalSpinner.close()
    }
  }, [LIMIT, apiStore])

  const patchChannelJoined = useCallback((item: any, is_joined: boolean) => {
    setList((prev) => {
      const next = prev.map((section) => ({
        ...section,
        data: section.data.map((channel) =>
          matchChannel(channel, item) ? { ...channel, is_joined } : channel,
        ),
      }))

      if (is_joined && !next.some((section) => section.key === "subscribed" && section.data.some((channel) => matchChannel(channel, item)))) {
        const subscribedIndex = next.findIndex((section) => section.key === "subscribed")
        if (subscribedIndex >= 0) {
          const subscribed = [...next[subscribedIndex].data]
          subscribed.unshift({ ...item, is_joined: true })
          next[subscribedIndex] = { ...next[subscribedIndex], data: subscribed }
        }
      }

      if (!is_joined) {
        const subscribedIndex = next.findIndex((section) => section.key === "subscribed")
        if (subscribedIndex >= 0) {
          next[subscribedIndex] = {
            ...next[subscribedIndex],
            data: next[subscribedIndex].data.filter((channel) => !matchChannel(channel, item)),
          }
        }
      }

      return syncSectionsJoinedState(next)
    })
  }, [])

  // 订阅频道
  const handleJoinChannel = useCallback((item) => {
    Dialog.show({
      title: '订阅频道',
      content: '确定要订阅该频道吗？',
      onConfirm: async () => {
        try {
          ensureLocalChatWhenJoiningGroup({
            id: item.id,
            chat_id: String(item.chat_id),
            title: item.title,
            image: item.image,
            isChannel: true,
          })
          await handleAddGroupMember({
            chat_id: item.id,
            chat_cid: item.chat_id,
            user_ids: currentUser?.id ? [currentUser.id] : [],
            chat_type: 2
          })
          const chat =
            chatStore.getChatByMessageRef?.({
              chat_id: item.id,
              chat_cid: String(item.chat_id),
            }) ?? chatStore.byChatId?.(String(item.chat_id))
          chat?.setDeleted(false)
          eventStore.publish(new Event(EventNames.CHAT_LIST, {}), { flags: FLAG_REPLY })
          patchChannelJoined(item, true)
          clearOptimisticExclusion(item, optimisticExclusionsRef.current)
          skipRemoteRefreshUntilRef.current = Date.now() + 1500
          globalToast.show("频道订阅成功")
          void refreshAllSections({ silent: true, force: true })
        } catch (error: any) {
          globalToast.show(error?.message || "频道订阅失败")
          throw error
        }
      }
    })
    
  }, [Dialog, chatStore, currentUser?.id, ensureLocalChatWhenJoiningGroup, eventStore, handleAddGroupMember, patchChannelJoined, refreshAllSections])

  // 取消订阅频道
  const handleKickOut = useCallback((item) => {
    Dialog.show({
      title: "取消订阅",
      content: `确定要取消订阅 ${item.title} 吗？`,
      onConfirm: async () => {
        try {
          const result = await handleMemberKickedOutGroup({ chat_id: item.id, chat_cid: item.chat_id })
          if (result) {
            patchChannelJoined(item, false)
            markOptimisticallyUnsubscribed(item, optimisticExclusionsRef.current)
            skipRemoteRefreshUntilRef.current = Date.now() + 1500
            const chat =
              chatStore.getChatByMessageRef?.({ chat_id: item.id, chat_cid: String(item.chat_id) }) ??
              chatStore.byChatId?.(String(item.chat_id)) ??
              (item.id != null ? chatStore.byId?.(item.id) : null)
            if (chat) {
              chatStore.remove(chat)
            }
            globalToast.show("取消订阅成功")
            void refreshAllSections({ silent: true, force: true })
          }
        } catch (error: any) {
          globalToast.show(error?.message || "取消订阅失败")
        }
          
      },
    })
    
  }, [Dialog, chatStore, handleMemberKickedOutGroup, patchChannelJoined, refreshAllSections])

  const isChannelCreatorForItem = useCallback(
    (item: any) => {
      if (isChannelCreator(item, currentUser?.id)) return true
      const cid = String(item?.chat_id ?? item?.name ?? "").trim()
      const chat =
        (cid
          ? chatStore.getChatByMessageRef?.({ chat_cid: cid, chat_id: item?.id ?? null }) ??
            chatStore.byChatId?.(cid)
          : null) ??
        (item?.id != null ? chatStore.byId?.(item.id) : null)
      if (chat?.creator?.id != null && chat.creator.id === currentUser?.id) return true
      if (chat?.isCurrentUserIsAdmin && chat.type === ChatTypes.CHANNEL) return true
      return false
    },
    [chatStore, currentUser?.id],
  )

  const onChannelChange = useCallback((item) => {
    if (item.is_joined) {
      if (isChannelCreatorForItem(item)) return
      handleKickOut(item)
    } else {
      handleJoinChannel(item)
    }
  }, [handleJoinChannel, handleKickOut, isChannelCreatorForItem])

  const handlePressChannelChat = useCallback(
    (item) => {
      if (!item?.is_joined) return
      const cid = String(item?.chat_id ?? item?.name ?? "").trim()
      const numericId = item?.id != null ? Number(item.id) : null
      if (!cid && numericId == null) return

      let chat =
        chatStore.getChatByMessageRef?.({
          chat_cid: cid || null,
          chat_id: numericId,
        }) ??
        (cid ? chatStore.byChatId?.(cid) || chatStore.chats?.get?.(cid) : null) ??
        (numericId != null ? chatStore.byId?.(numericId) : null)

      if (!chat) {
        chat = chatStore.newChannelChat?.({
          chat_id: cid || String(numericId),
          id: numericId ?? undefined,
          name: cid || undefined,
          type: ChatTypes.CHANNEL,
          title: item?.title || "频道",
          desc: item?.desc || null,
          image: item?.image || "",
          members: [],
          created: item?.created || Date.now(),
          updated: item?.updated || item?.created || Date.now(),
        } as any)
      }

      if (!chat?.chat_id) return

      if (chat.deleted) {
        chatStore.reviveChat?.(chat)
      } else {
        chat.setDeleted?.(false)
      }
      navigation.navigate("Chat", { chat_id: chat.chat_id })
    },
    [chatStore, navigation],
  )

  const skipNextFocusRefreshRef = useRef(true)

  useEffect(() => {
    canLoadMoreRef.current = false
    void refreshAllSections({ silent: false }).finally(() => {
      window.setTimeout(() => {
        canLoadMoreRef.current = true
      }, 500)
    })
  }, [refreshAllSections])

  useFocusEffect(
    useCallback(() => {
      if (skipNextFocusRefreshRef.current) {
        skipNextFocusRefreshRef.current = false
        return
      }
      if (Date.now() < skipRemoteRefreshUntilRef.current) return
      void refreshAllSections({ silent: true })
    }, [refreshAllSections]),
  )

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(REFRESH_CHANNEL_LIST_EVENT, () => {
      if (Date.now() < skipRemoteRefreshUntilRef.current) return
      void refreshAllSections({ silent: true })
    })
    return () => sub.remove()
  }, [refreshAllSections])

  const filteredSections = useMemo(() => {
    if (!searchText.trim()) return list

    const keyword = searchText.toLowerCase()

    return list
      .map(section => {
        const filteredData = section.data.filter(item =>
          item.title?.toLowerCase().includes(keyword) ||
          item.desc?.toLowerCase().includes(keyword)
        )

        return {
          ...section,
          data: filteredData,
        }
      })
      .filter(section => section.data.length > 0)

  }, [list, searchText])

  const keyExtractor = useCallback(
    (item, idx) => `${item.id ?? ""}-${item.chat_id ?? item.name ?? ""}-${idx}`,
    [],
  )
  const renderItem = useCallback(({ item, index }) => (
    <ColumnFlex
      height="auto"
      bg={theme.colors.blockBackground}
      borderRadius="8px"
      overflow="hidden"
    >
      <ChannelMembersItem
        item={item}
        isCreator={isChannelCreatorForItem}
        onClick={() => onChannelChange(item)}
        onPressChat={handlePressChannelChat}
      />
    </ColumnFlex>
  ),[handlePressChannelChat, isChannelCreatorForItem, onChannelChange, theme.colors.blockBackground])

  const loadingMore = pagination.subscribed.loadingMore

  const handleEndReached = useCallback(() => {
    if (!canLoadMoreRef.current || loadingMore) return

    if (pagination.subscribed.hasMore) {
      void getSectionList("subscribed", {
        silent: true,
        page: pagination.subscribed.page + 1,
        append: true,
      })
      return
    }

    // if (pagination.recommended.hasMore) {
    //   void getSectionList("recommended", {
    //     silent: true,
    //     page: pagination.recommended.page + 1,
    //     append: true,
    //   })
    // }
  }, [getSectionList, loadingMore, pagination.subscribed.hasMore, pagination.subscribed.page])

  const Footer = useMemo(() => {
    const hasAnyData = list.some((section) => section.data.length > 0)
    if (!hasAnyData) return null

    if (loadingMore) {
      return (
        <CenterFlex padding="12px 0">
          <ActivityIndicator />
          <Text ml="8px" color={theme.colors.secondaryText} fontSize="12px">
            加载中…
          </Text>
        </CenterFlex>
      )
    }

    if (!pagination.subscribed.hasMore) {
      return (
        <CenterFlex padding="12px 0">
          <Text color={theme.colors.secondaryText} fontSize="12px">
            没有更多了
          </Text>
        </CenterFlex>
      )
    }
    return null
  }, [list, loadingMore, pagination.subscribed.hasMore, theme.colors.secondaryText])

  return (
    <Container>
        <ColumnFlex   minHeight="max-content" height="auto" width="100%" padding="10px" bg={theme.colors.blockBackground}>
            <SearchBar
                value={searchText}
                placeholder="搜索频道"
                style={{
                    backgroundColor: theme.colors.background,
                    minHeight: 'max-content',
                }}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
            />
        </ColumnFlex>
        <ColumnFlex
            flex={1}
            minHeight={0}
            width="100%"
            padding="0 12px"
            style={{ paddingBottom: insets.bottom }}
        > 
            <SectionList
                style={{ flex: 1, minHeight: 0, width: "100%" }}
                sections={filteredSections}
                keyExtractor={keyExtractor}
                contentContainerStyle={{ flexGrow: 1, paddingBottom: insets.bottom + 60 }}
                renderItem={renderItem}
                renderSectionHeader={({section: {title}}) => (
                    <ColumnFlex height="auto" width="100%" padding="8px 0">
                        <Text color={theme.colors.secondary} fontSize="12px">{title}</Text>
                    </ColumnFlex>
                )}
                onEndReached={handleEndReached}
                onEndReachedThreshold={0.2}
                ListEmptyComponent={<NoData title="暂无频道" content="订阅频道后将显示在这里" />}
                ListFooterComponent={Footer}
            />
        </ColumnFlex>
      </Container>
  )
}

export default ChannelList
